import { Component, HostListener, inject } from '@angular/core';
import { NgSwitch, NgSwitchCase } from '@angular/common';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  Event,
  RouterOutlet,
} from '@angular/router';

import { filter, map, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { S1mAngularBootstrapLibModule } from 's1m-angular-bootstrap-lib';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';

import { environment } from 'src/environments/environment';
import { LocalStorageService } from './shared/services/local-storage.service';
import { DocumentClickService } from './shared/services/document-click.service';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { LoaderComponent } from './shared/loader/loader.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [NgSwitch, NgSwitchCase, RouterOutlet, LoaderComponent, S1mAngularBootstrapLibModule],
})
export class AppComponent {
  router = inject(Router);
  translateService = inject(TranslateService);
  bsLocaleService = inject(BsLocaleService);
  localStorage = inject(LocalStorageService);
  documentClickService = inject(DocumentClickService);
  iconLibrary = inject(FaIconLibrary);
  loading!: boolean;

  @HostListener('document:click', ['$event'])
  documentClick(event: PointerEvent) {
    if (event.target instanceof HTMLElement) {
      this.documentClickService.documentClickedTarget.next(event.target);
    }
  }

  constructor() {
    this.listenToInitialNavigationEvents();
    this.addIcons();
    this.initLanguageSettings();
  }

  private listenToInitialNavigationEvents() {
    const navigationDone$ = this.router.events.pipe(
      map((event: RouterEvent | Event) => event as RouterEvent),
      filter((routerEvent) => routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationError),
    );

    this.router.events
      .pipe(
        takeUntil(navigationDone$),
        map((event: RouterEvent | Event) => event as RouterEvent),
        filter((routerEvent) => routerEvent instanceof NavigationStart),
      )
      .subscribe({
        next: () => (this.loading = true),
        complete: () => (this.loading = false),
      });
  }

  private addIcons() {
    this.iconLibrary.addIconPacks(fas);
  }

  private initLanguageSettings() {
    const lang = this.localStorage.language || environment.language;
    this.translateService.setDefaultLang('en'); // fallback
    this.translateService.use(lang);
    this.bsLocaleService.use(lang);
  }
}